'use client';

import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';

/**
 * WpManifestoSection — `.wp` "Why Invest Pre-IPO" manifesto band.
 *
 * Dark night-surface band with a teal radial glow, a gold eyebrow, a large
 * centered serif headline, three supporting body columns (with hairline tops),
 * and a champagne CTA button. Distinct from EditorialQuoteSection (`.ps` band).
 *
 * Source: design-refs/source/assets/private-home.css (`.wp`, `.wp-inner`,
 * `.wp-eyebrow`, `.wp-head`, `.wp-cols`, `.wp-col`, `.wp-body`, `.wp-btn`) +
 * private-home.js `buildWhyPrivate()`.
 */

export interface ManifestoColumn {
  /**
   * Column body copy. May include an emphasised fragment (rendered italic /
   * champagne) via the `<em>` element or a React node.
   */
  body: ReactNode;
}

export interface WpManifestoSectionProps {
  /** Gold tracked eyebrow above the headline. */
  eyebrow?: string;
  /** Large centered serif headline. */
  headline?: ReactNode;
  /** Supporting body columns (3 in the source). */
  columns?: ManifestoColumn[];
  /** CTA button label. */
  ctaLabel?: string;
  /** CTA button click handler. */
  onCtaClick?: () => void;
  /** Muted legal note beneath the CTA. */
  disclaimer?: string;
  /**
   * Element for the headline. Defaults to `h2`.
   *
   * The SE3 homepage gates `HeroSection` — which owns the page's only `h1` —
   * behind `isSignedIn === false`, and `useIsSignedIn` resolves on the client.
   * During the server render the state is `null`, so no `h1` reaches the HTML
   * at all and crawlers see a document that starts at `h2`. This band renders
   * unconditionally, so promoting its headline gives the page exactly one `h1`
   * in every auth state without changing what anyone sees.
   */
  headingLevel?: 'h1' | 'h2';
  className?: string;
  'data-cy'?: string;
}

const DEFAULT_EYEBROW = '— Why Invest Pre-IPO —';

const DEFAULT_HEADLINE = 'Private-market access used to happen behind closed doors.';

/* `.wp-body em` — italic champagne emphasis. */
const Em = styled('em')({
  fontStyle: 'italic',
  color: COLORS.champagne,
});

const DEFAULT_COLUMNS: ManifestoColumn[] = [
  {
    body: "Companies are waiting longer than ever to go public — and that's where the real value gets built.",
  },
  {
    body: 'For most people, investing in that phase simply wasn’t an option. It was reserved for major venture capital firms and institutional investors.',
  },
  {
    body: (
      <>
        <Em>StartEngine changes that.</Em> Now you can invest in the startups and growth companies
        shaping the future.
      </>
    ),
  },
];

const DEFAULT_CTA_LABEL = 'Gain Access';

const DEFAULT_DISCLAIMER =
  'Investments in private companies are speculative, illiquid, and involve a high degree of risk, including the possible loss of your entire investment. Most early-stage and pre-IPO companies do not achieve an IPO or other liquidity event. Historical figures and past performance do not predict future results. No return, liquidity event, or IPO is promised or guaranteed.';

/* `.wp` */
const Band = styled('section')({
  position: 'relative',
  overflow: 'hidden',
  background: COLORS.night,
  borderTop: `1px solid ${COLORS.gold}`,
  /* `.wp::before` — teal radial glow. */
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(120% 130% at 82% 0%, rgba(1,209,184,0.08), transparent 55%)',
    pointerEvents: 'none',
  },
});

/* `.wp-inner` */
const Inner = styled('div')({
  position: 'relative',
  zIndex: 1,
  maxWidth: '1060px',
  margin: '0 auto',
  padding: '92px var(--pad)',
  textAlign: 'center',
  '@media (max-width: 768px)': {
    padding: '60px var(--pad)',
  },
});

/* `.wp-eyebrow` */
const Eyebrow = styled('div')({
  fontFamily: FONTS.sans,
  fontWeight: 600,
  fontSize: '12px',
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: COLORS.gold,
  marginBottom: '22px',
});

/* `.wp-head` */
const Head = styled('h2')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '40px',
  lineHeight: 1.14,
  letterSpacing: '-0.012em',
  color: '#fff',
  textWrap: 'balance',
  maxWidth: '780px',
  margin: '0 auto',
  '@media (max-width: 768px)': {
    fontSize: '31px',
  },
});

/* `.wp-cols` */
const Cols = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '36px',
  marginTop: '56px',
  textAlign: 'left',
  '@media (max-width: 1080px)': {
    gridTemplateColumns: '1fr',
    gap: '28px',
  },
});

/* `.wp-col` */
const Col = styled('div')({
  borderTop: '1px solid rgba(233,215,182,0.28)',
  paddingTop: '24px',
});

/* `.wp-body` */
const Body = styled('p')({
  margin: 0,
  fontFamily: FONTS.sans,
  fontSize: '16.5px',
  lineHeight: 1.72,
  color: COLORS.onDark2,
  textWrap: 'pretty',
  '@media (max-width: 768px)': {
    fontSize: '17px',
  },
});

/* `.wp-btn` */
const Btn = styled('button')({
  marginTop: '54px',
  whiteSpace: 'nowrap',
  fontFamily: FONTS.sans,
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: COLORS.night,
  background: COLORS.champagne,
  border: 'none',
  padding: '16px 34px',
  borderRadius: '14px',
  cursor: 'pointer',
  transition: 'background 0.15s, transform 0.15s',
  '&:hover': {
    background: '#f3e6cb',
    transform: 'translateY(-1px)',
  },
});

const Disclaimer = styled('p')({
  margin: '44px auto 0',
  fontFamily: FONTS.sans,
  fontSize: '12px',
  color: 'rgba(255,255,255,0.4)',
  lineHeight: 1.6,
  maxWidth: '860px',
  textAlign: 'center',
  textWrap: 'pretty',
});

export function WpManifestoSection({
  eyebrow = DEFAULT_EYEBROW,
  headline = DEFAULT_HEADLINE,
  columns = DEFAULT_COLUMNS,
  ctaLabel = DEFAULT_CTA_LABEL,
  onCtaClick,
  disclaimer = DEFAULT_DISCLAIMER,
  headingLevel = 'h2',
  className,
  'data-cy': dataCy = 'wp-manifesto-section',
}: WpManifestoSectionProps) {
  return (
    <Band className={`wp ${className ?? ''}`.trim()} data-cy={dataCy}>
      <Inner className="wp-inner">
        {eyebrow && (
          <Eyebrow className="wp-eyebrow" data-cy="wp-manifesto-eyebrow">
            {eyebrow}
          </Eyebrow>
        )}
        <Head as={headingLevel} className="wp-head" data-cy="wp-manifesto-headline">
          {headline}
        </Head>
        {columns.length > 0 && (
          <Cols className="wp-cols" data-cy="wp-manifesto-columns">
            {columns.map((column, index) => (
              <Col key={index} className="wp-col">
                <Body className="wp-body">{column.body}</Body>
              </Col>
            ))}
          </Cols>
        )}
        {ctaLabel && (
          <Btn type="button" className="wp-btn" onClick={onCtaClick} data-cy="wp-manifesto-cta">
            {ctaLabel}
          </Btn>
        )}
        {disclaimer && <Disclaimer data-cy="wp-manifesto-disclaimer">{disclaimer}</Disclaimer>}
      </Inner>
    </Band>
  );
}

export default WpManifestoSection;
